import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Input, Form, Modal, Typography } from "antd";

import CustomSelect from "@/components/inputs/CustomSelect";

import { createFolder, updateFolder, getFoldersList } from "@/api/files";

const { Text } = Typography;

// Окно для создания/редактирования папки, содержит форму
const FolderModal = ({ data = {}, visible, onSave, onCancel, units = [] }) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const location = useLocation();
  const isAdmins = location.pathname.startsWith("/administration");

  const unitUuid = Form.useWatch("unit_uuid", form);
  const [foldersOptions, setFoldersOptions] = useState([]);

  const fetchFoldersList = async (unit_uuid) => {
    try {
      const res = await getFoldersList(
        {
          units_uuids: [unit_uuid],
        },
        isAdmins,
      );

      setFoldersOptions(
        res
          .filter(({ uuid }) => uuid !== data?.uuid)
          .map((item) => {
            const fullPath = item.fullPath
              ? `Файлы/${item.fullPath}/${item.name}`
              : `Файлы/${item.name}`;

            return {
              ...item,
              label: item.name,
              display_label: fullPath,
              value: item.uuid,
            };
          }),
      );
    } catch (error) {
      if (data?.folder) {
        const fullPath = data.folder.fullPath
          ? `Файлы/${data.folder.fullPath}/${data.folder.name}`
          : `Файлы/${data.folder.name}`;

        setFoldersOptions([
          {
            ...data.folder,
            label: data.folder.name,
            display_label: fullPath,
            value: data.folder.uuid,
          },
        ]);
      } else {
        form.setFieldValue("folder_uuid", null);
      }
    }
  };

  useEffect(() => {
    if(!unitUuid) return;
    fetchFoldersList(unitUuid);

    // eslint-disable-next-line
  }, [unitUuid]);

  const onValuesChange = async (changedValues) => {
    // Проверяем, изменилось ли РГ
    if ("unit_uuid" in changedValues) {
      // сбрасываем родительскую папку
      form.setFieldsValue({
        parent_uuid: undefined,
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    try {
      if (data?.uuid) {
        await updateFolder({ ...data, ...values });
      } else {
        await createFolder(values);
      }

      onSave();
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  return (
    <Modal
      maskClosable={false}
      title={data?.uuid ? "Редактирование папки" : "Добавление новой папки"}
      open={visible}
      onOk={onOk}
      onCancel={handleCancel}
      okText={data?.uuid ? "Сохранить" : "Добавить"}
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={data}
        onFinish={handleSave}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: "Пожалуйста, введите название" }]}
        >
          <Input disabled={data?.is_system} />
        </Form.Item>

        <Form.Item name="description" label="Описание">
          <Input.TextArea />
        </Form.Item>

        <Form.Item
          name="unit_uuid"
          label="Рабочая группа"
          rules={[
            { required: true, message: "Пожалуйста, выберите рабочую группу" },
          ]}
        >
          <CustomSelect
            placeholder="Начните вводить"
            options={units}
            fieldNames={{
              label: "name",
              value: "uuid",
              key: "uuid",
            }}
            showSearch
            optionFilterProp="name"
            disabled={data?.uuid}
          />
        </Form.Item>

        <Form.Item
          name="parent_uuid"
          label="Родительская папка"
          rules={[
            { required: !data?.is_root, message: "Пожалуйста, выберите папку" },
          ]}
        >
          <CustomSelect
            placeholder="Начните вводить"
            disabled={data?.is_system}
            options={foldersOptions}
            showSearch
            allowClear
            fieldNames={{ value: "uuid" }}
            optionFilterProp="label"
            optionLabelProp="display_label"
            optionRender={(option) => (
              <>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {option.data.label}
                </div>
                <div style={{ fontSize: 9, color: "#999" }}>
                  {option.data.display_label}
                </div>
              </>
            )}
          />
        </Form.Item>

        {error && (
          <div>
            <Text type="danger">{error}</Text>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default FolderModal;
